import { flatten, map } from 'lodash';
import { permissions } from '../constants/permissions';
import useAuth from './useAuth';

export function useMaxDrawExtent () {
    const auth = useAuth();
    const drawExtents = flatten(map(auth?.user?.roles, 'permissions'))
        .filter(permission => permission.type === permissions.AOI_DRAW && permission.extentMetres)
        .map(drawPermission => drawPermission.extentMetres);
    return Math.max(...drawExtents, 0);
}

// Fallback dimensions when no AOI_DRAW_LINE or AOI_VIEW_LINE permission is found.
export const DEFAULT_LINE_DIMENSIONS = { length: 175, width: null, buffer: 12.5 };

/**
 * Returns the line draw dimensions { length, buffer } from the user's
 * AOI_DRAW_LINE permission. When multiple permissions exist, returns the most
 * permissive (largest length). Falls back to DEFAULT_LINE_DIMENSIONS.
 */
export function useLineDrawDimensions () {
    const auth = useAuth();
    const lineDrawPermissions = flatten(map(auth?.user?.roles, 'permissions'))
        .filter(permission => permission.type === permissions.AOI_DRAW_LINE && permission.dimension);

    if (lineDrawPermissions.length === 0) {
        return DEFAULT_LINE_DIMENSIONS;
    }

    return lineDrawPermissions.reduce((best, p) => {
        return p.dimension.length > best.dimension.length ? p : best;
    }).dimension;
}

/**
 * Returns the line view dimensions { length, buffer } from the user's
 * AOI_VIEW_LINE permission. Used to validate line AOIs before sending a view
 * request. When multiple permissions exist, returns the most permissive
 * (largest length). Falls back to DEFAULT_LINE_DIMENSIONS.
 */
export function useLineViewDimensions () {
    const auth = useAuth();
    const lineViewPermissions = flatten(map(auth?.user?.roles, 'permissions'))
        .filter(permission => permission.type === permissions.AOI_VIEW_LINE && permission.dimension);

    if (lineViewPermissions.length === 0) {
        return DEFAULT_LINE_DIMENSIONS;
    }

    return lineViewPermissions.reduce((best, p) => {
        return p.dimension.length > best.dimension.length ? p : best;
    }).dimension;
}

/**
 * @deprecated Width is no longer used for line dimensions.
 * Kept for backward compatibility — always returns null.
 */
export function useMaxLineWidthMetres () {
    return null;
}

export function useHasUserPermission (permission) {
    const { user } = useAuth();
    const userPermissions = map(flatten(map(user?.roles, 'permissions')), 'type');
    return userPermissions.includes(permission);
}

export default { useMaxDrawExtent, useLineDrawDimensions, useLineViewDimensions, useMaxLineWidthMetres, useHasUserPermission };
